import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, Gift, Home, UserRound, WalletCards } from 'lucide-react';
import type { PlayerRoute } from '../../app/appStore';
import { useAppStore } from '../../app/appStore';
import { useAuthStore } from '../../features/auth/authStore';
import { fetchNotifications } from '../../features/notifications/api';
import { fetchActivitiesSummary, fetchActivityAudits, fetchActivityDrum, fetchRewardInbox } from '../../features/profile/api';
import { checkinDayRewards, checkinRewardText, checkinTileLabel, resolveCheckinTrackDays } from '../profile/checkinDays';
import { fetchFeedbackSummary } from '../../features/support/feedback/api';
import { fetchMyCoupons } from '../../features/wallet/api';
import { fmtAmount, fmtMoney } from '../lib/format';
import { useI18nStore } from '../i18n/i18nStore';
import { buildDailyPopupModel } from './dailyPopupView';
import { buildWelcomePopupModel, type WelcomeStep } from './welcomePopupView';

const NAV_ITEMS: Array<{
  route: PlayerRoute;
  labelKey: 'hall' | 'wallet' | 'rewardsTab' | 'avisos' | 'me';
  icon: typeof Home;
}> = [
  { route: 'lobby', labelKey: 'hall', icon: Home },
  { route: 'wallet', labelKey: 'wallet', icon: WalletCards },
  { route: 'rewards', labelKey: 'rewardsTab', icon: Gift },
  { route: 'avisos', labelKey: 'avisos', icon: Bell },
  { route: 'me', labelKey: 'me', icon: UserRound },
];

const DAILY_POPUP_KEY = 'sorteia:dailyRewardsPopup';
// Once per account lifetime: the welcome-coupons announcement. Not per-day —
// a gift is announced once, then it lives in the Cupons tab.
const WELCOME_COUPONS_KEY = 'sorteia:welcomeCouponsPopup';
function todayKey() {
  const d = new Date();
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}

// "+30%" for a percentage rung, "+R$ 10" for a fixed one. No decimals unless
// the value has them — "+15%" not "+15.0%".
function bonusLabel(step: WelcomeStep): string {
  if (step.bonusType === 'percent') return `+${fmtAmount(Math.round(step.bonusValue * 1000) / 10)}%`;
  return `+${fmtMoney(step.bonusValue)}`;
}

export function BottomNav() {
  const route = useAppStore((state) => state.route);
  const setRoute = useAppStore((state) => state.setRoute);
  const setActiveRoomId = useAppStore((state) => state.setActiveRoomId);
  const setActiveOrderId = useAppStore((state) => state.setActiveOrderId);
  const setActiveProfileUserId = useAppStore((state) => state.setActiveProfileUserId);
  const setActiveArchiveRoundNumber = useAppStore((state) => state.setActiveArchiveRoundNumber);
  const setWalletTargetAction = useAppStore((state) => state.setWalletTargetAction);
  const setAvisosTargetView = useAppStore((state) => state.setAvisosTargetView);
  const setMeSecurityTargetChannel = useAppStore((state) => state.setMeSecurityTargetChannel);
  const setWalletTicketsTarget = useAppStore((state) => state.setWalletTicketsTarget);
  const token = useAuthStore((state) => state.token);
  const guestMode = useAuthStore((state) => state.guestMode);
  const t = useI18nStore((state) => state.t);
  useI18nStore((state) => state.language);
  const notificationsQuery = useQuery({
    queryKey: ['notifications', token],
    queryFn: () => fetchNotifications(token),
    enabled: !!token && !guestMode,
    staleTime: 20_000,
  });
  // Same staleTime as notificationsQuery above: the bottom-nav badge is a
  // sum of notifications-unread + feedback-unread, so both halves must
  // refresh on the same cadence or the number visibly jumps twice.
  // FeedbackPanel/FeedbackDetail (Task 15) invalidate this same
  // ['feedback-summary', token] key on mark-read, which is what lets this
  // badge and the Feedback tab's own dot clear together.
  const feedbackSummaryQuery = useQuery({
    queryKey: ['feedback-summary', token],
    queryFn: () => fetchFeedbackSummary(token || ''),
    enabled: !!token && !guestMode,
    staleTime: 20_000,
  });
  const unreadCount = Number(
    notificationsQuery.data?.unread ??
    (notificationsQuery.data?.items || []).filter((item) => !item.read).length,
  ) + Number(feedbackSummaryQuery.data?.unread || 0);
  const activitiesQuery = useQuery({
    queryKey: ['activities-summary', token],
    queryFn: () => fetchActivitiesSummary(token),
    enabled: !!token && !guestMode,
    staleTime: 20_000,
  });
  // The Rewards tab needs two more calls the badge does not. Warming them
  // while the player is still on another tab means the tab opens onto its
  // cards, not onto a loading state — the summary above is already cached
  // for the badge, so this closes the gap for the other two.
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!token) return;
    void queryClient.prefetchQuery({ queryKey: ['activity-drum', token], queryFn: () => fetchActivityDrum(token), staleTime: 30_000 });
    void queryClient.prefetchQuery({ queryKey: ['activity-audits', token], queryFn: () => fetchActivityAudits(token, 10), staleTime: 30_000 });
  }, [queryClient, token]);

  // Same resolver as the Rewards page, so the popup never announces a
  // check-in the page does not show (or prices it differently).
  const activityConfigs = activitiesQuery.data?.configs;
  const checkinTrackDays = useMemo(() => resolveCheckinTrackDays(activityConfigs), [activityConfigs]);
  const [popupClosed, setPopupClosed] = useState(false);
  let seenToday = false;
  try {
    seenToday = typeof localStorage !== 'undefined' && localStorage.getItem(DAILY_POPUP_KEY) === todayKey();
  } catch {
    seenToday = false;
  }
  const rewardInboxQuery = useQuery({
    queryKey: ['reward-inbox', token],
    queryFn: () => fetchRewardInbox(token || ''),
    enabled: !!token && !guestMode,
    staleTime: 20_000,
  });
  const hasClaimableReward = (rewardInboxQuery.data?.claimable || []).length > 0;
  const inboxCash = Number(rewardInboxQuery.data?.summary?.claimableCashTotal
    ?? (rewardInboxQuery.data?.claimable || []).reduce((sum, item) => sum + Number(item.cashAmount || 0), 0));
  const checkins = activitiesQuery.data?.checkins;
  const dailyPopup = useMemo(
    () => buildDailyPopupModel({ checkins, trackDays: checkinTrackDays, inboxCash }),
    [checkins, checkinTrackDays, inboxCash],
  );
  const hasClaimableCheckin = dailyPopup !== null;
  const rewardsDot = !guestMode && (hasClaimableCheckin || hasClaimableReward);
  // The welcome gift: every new account is seeded with the 1st-5th deposit
  // bonus coupons. Announce them once, BEFORE the first deposit — the moment
  // they can still change what that deposit is worth. The popup only fires if
  // the coupons actually exist on the account (never a promise from config
  // alone) and the player has never deposited.
  const depositCount = Number(activitiesQuery.data?.depositCount ?? -1);
  const couponsQuery = useQuery({
    queryKey: ['coupons-mine', token],
    queryFn: () => fetchMyCoupons(token || ''),
    enabled: !!token && !guestMode && depositCount === 0,
    staleTime: 60_000,
  });
  // The gift is the whole ladder — one rung is available today, the rest are
  // locked behind future deposits, but all five were granted.
  const welcomeCoupons = couponsQuery.data?.items;
  const welcomePopup = useMemo(() => buildWelcomePopupModel(welcomeCoupons), [welcomeCoupons]);
  const [welcomeClosed, setWelcomeClosed] = useState(false);
  let welcomeSeen = true;
  try {
    welcomeSeen = typeof localStorage !== 'undefined' && localStorage.getItem(WELCOME_COUPONS_KEY) === '1';
  } catch {
    welcomeSeen = true;
  }
  const showWelcomePopup = !guestMode && depositCount === 0 && welcomePopup !== null && !welcomeClosed && !welcomeSeen;
  // The one action is depositing: the coupon is applied on the deposit screen
  // (it picks the best one for the amount), so "see my coupons" was a detour.
  function dismissWelcomePopup(goDeposit: boolean) {
    try {
      localStorage.setItem(WELCOME_COUPONS_KEY, '1');
    } catch {
      /* ignore */
    }
    setWelcomeClosed(true);
    if (goDeposit) {
      goToMainTab('wallet');
      setWalletTargetAction('deposit');
    }
  }
  // One popup at a time; the gift outranks the daily nudge (which returns
  // tomorrow — the gift moment does not).
  const showDailyPopup = !guestMode && dailyPopup !== null && !popupClosed && !seenToday && !showWelcomePopup;
  function dismissDailyPopup(goRewards: boolean) {
    try {
      localStorage.setItem(DAILY_POPUP_KEY, todayKey());
    } catch {
      /* ignore */
    }
    setPopupClosed(true);
    if (goRewards) goToMainTab('rewards');
  }

  function goToMainTab(nextRoute: PlayerRoute) {
    // Tapping the tab you are already on is a no-op, not a navigation. Without
    // this it pushed a duplicate entry every time — five taps on "Me" meant five
    // back presses to undo them.
    if (route === nextRoute) return;
    // These clears run while the OLD route is still current, so each one that
    // matches it writes the URL again — and they defaulted to push. Tapping a
    // tab from /wallet, /pedidos, /leaderboard, /archive or /wallet/tickets
    // therefore cost two history entries instead of one. Replace: they are
    // tidying up the entry we are about to leave, not creating a step.
    setActiveRoomId(null);
    setActiveOrderId(null);
    setActiveProfileUserId(null, { replace: true });
    setActiveArchiveRoundNumber(null, { replace: true });
    setWalletTargetAction(null, { replace: true });
    setAvisosTargetView(null);
    setMeSecurityTargetChannel(null, { replace: true });
    setWalletTicketsTarget(null, null, { replace: true });
    // The tab bar is a switcher between five peers, not a stack. Pushing made
    // the back button replay the user's whole tab history — a dozen taps meant
    // a dozen presses to get out of the app — while the on-screen back arrows
    // push as well, so the stack only ever grew. Replacing keeps one tab entry
    // and lets back mean "leave", which is what a tab bar implies.
    setRoute(nextRoute, { replace: true });
  }

  return (
    <>
    {showWelcomePopup && welcomePopup ? (() => {
      const pct = bonusLabel(welcomePopup.headline);
      return (
      <div className="sheet-backdrop daily-popup-backdrop" role="presentation" onMouseDown={() => dismissWelcomePopup(false)} data-welcome-popup-react="true">
        <section className="daily-popup" role="dialog" aria-modal="true" aria-label={t('welcomePopupTitle')} onMouseDown={(e) => e.stopPropagation()}>
          <p className="daily-popup-kicker">{t('welcomePopupTitle')}</p>
          <p className="welcome-popup-pct" data-welcome-popup-pct="true">{pct}</p>
          <p className="welcome-popup-sub">{t('welcomePopupSub')}</p>
          <ol className="welcome-ladder" aria-label={t('welcomePopupNote').replace('%n%', String(welcomePopup.steps.length))}>
            {welcomePopup.steps.map((step, i) => (
              <li key={i} className={`welcome-step${step.state === 'now' ? ' is-now' : ''}`}>
                <b>{bonusLabel(step)}</b>
                <span>{i + 1}º</span>
              </li>
            ))}
          </ol>
          <p className="welcome-popup-note">
            {t('welcomePopupNote').replace('%n%', String(welcomePopup.steps.length))}
            {welcomePopup.noExpiry ? t('welcomePopupNoExpiry') : ''}
          </p>
          <button className="primary-button" type="button" onClick={() => dismissWelcomePopup(true)}>{t('welcomePopupDeposit').replace('%v%', pct)}</button>
          <button className="daily-popup-later" type="button" onClick={() => dismissWelcomePopup(false)}>{t('dailyPopupLater')}</button>
        </section>
      </div>
      );
    })() : null}
    {showDailyPopup && dailyPopup ? (() => {
      const { primary } = dailyPopup;
      const headline = checkinRewardText(dailyPopup.todayRewards, t) || t('dailyPopupTitle');
      const kicker = t('dailyPopupDayOf').replace('%n%', String(primary.streakDay))
        + (dailyPopup.claimable.length > 1 ? ` · ${t('dailyPopupMulti').replace('%k%', String(dailyPopup.claimable.length))}` : '');
      // Day 1 has no streak to lose, so it sells the week; day 7 is the peak;
      // in between, loss beats promise — say where a missed day lands you. The
      // warning is true: the backend resets on any missed day.
      const warn = primary.streakDay === 1
        ? t('activitiesCheckinAskWeek').replace('%v%', checkinRewardText(dailyPopup.weekRewards, t))
        : primary.streakDay >= 7
          ? t('activitiesCheckinAskDay7')
          : t('dailyPopupResetWarn').replace('%v%', checkinRewardText(dailyPopup.restartRewards, t));
      return (
      <div className="sheet-backdrop daily-popup-backdrop" role="presentation" onMouseDown={() => dismissDailyPopup(false)} data-daily-popup-react="true">
        <section className="daily-popup" role="dialog" aria-modal="true" aria-label={t('dailyPopupTitle')} onMouseDown={(e) => e.stopPropagation()}>
          <p className="daily-popup-kicker">{kicker}</p>
          <p className="daily-popup-amount" data-daily-popup-amount="true">{headline}</p>
          <p className="daily-popup-ready">{t('dailyPopupReady')}</p>
          <ol className="daily-popup-strip" aria-label={t('activitiesCheckinsTitle')}>
            {dailyPopup.tiles.map((tile) => (
              <li key={tile.day.day} className={`daily-popup-tile is-${tile.state}`} title={checkinRewardText(checkinDayRewards(tile.day), t)}>
                <i aria-hidden="true" />
                <b>{checkinTileLabel(tile.day, { compact: true })}</b>
                <span>{tile.state === 'today' ? t('activitiesCheckinTodayTile') : String(tile.day.day)}</span>
              </li>
            ))}
          </ol>
          <p className="daily-popup-warn">{warn}</p>
          {dailyPopup.inboxCash > 0 ? (
            <p className="daily-popup-extra">{t('dailyPopupInboxExtra').replace('%v%', fmtMoney(dailyPopup.inboxCash))}</p>
          ) : null}
          <button className="primary-button" type="button" onClick={() => dismissDailyPopup(true)}>{t('dailyPopupClaim').replace('%v%', headline)}</button>
          <button className="daily-popup-later" type="button" onClick={() => dismissDailyPopup(false)}>{t('dailyPopupLater')}</button>
        </section>
      </div>
      );
    })() : null}
    <nav className="main-bottom-nav" aria-label={t('mainNavigation')} data-player-nav="bottom">
      {NAV_ITEMS.map((item) => {
        const Icon = item.icon;
        const active =
          route === item.route ||
          ((route === 'pedidos' || route === 'pedidos.unpaid' || route === 'wallet.tickets') && item.route === 'wallet') ||
          ((route === 'bet-history' || route.startsWith('me.')) && item.route === 'me') ||
          (route === 'leaderboard' && item.route === 'lobby') ||
          (route === 'lobby.next' && item.route === 'lobby') ||
          ((route === 'avisos.faq' || route === 'avisos.help' || route === 'avisos.feedback') && item.route === 'avisos');
        return (
          <button
            key={item.route}
            className={active ? 'active' : ''}
            type="button"
            aria-current={active ? 'page' : undefined}
            data-nav-route={item.route === 'lobby' ? 'hall' : item.route}
            onClick={() => {
              // While the redesigned hall is being previewed, Hall returns there
              // rather than kicking the reviewer back to the current lobby.
              goToMainTab(item.route === 'lobby' && route === 'lobby.next' ? 'lobby.next' : item.route);
            }}
          >
            <span className="nav-icon-wrap">
              <Icon size={20} aria-hidden="true" />
              {item.route === 'avisos' && unreadCount > 0 ? <b className="nav-badge">{unreadCount > 9 ? '9+' : unreadCount}</b> : null}
              {item.route === 'rewards' && rewardsDot ? <b className="nav-badge is-dot" aria-hidden="true" /> : null}
            </span>
            <span>{t(item.labelKey)}</span>
          </button>
        );
      })}
    </nav>
    </>
  );
}
